import { Pressable, View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Feature } from '@fytness/shared';
import { Icon } from '~/global/components/icon/Icon';
import { useModal } from '@/contexts/ModalContext';
import { useFeatureGuard } from '~/purchases/hooks/useFeatureGuard';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { SportActivityRecord } from '~/powersync/generated/schema';
import { SportActivityFormDialog } from '~/sport-activity/components/sport-activity-form-dialog/SportActivityFormDialog';
import { SportActivityListItem } from '~/sport-activity/components/sport-activity-list-item/SportActivityListItem';

export interface ExerciseLibrarySectionProps extends ViewProps {}

export const ExerciseLibrarySection = ({
  className,
  ...props
}: ExerciseLibrarySectionProps) => {
  const { t } = useTranslation('workout');
  const { showModal } = useModal();
  const guard = useFeatureGuard();
  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;

  const { data: activities } = useLivePowerSyncQuery<SportActivityRecord>(
    db.SportActivity.queries.findByUserId(currentUserId),
    [currentUserId],
  );

  const handleAdd = () => {
    // Creating a custom exercise is a Pro feature — non-entitled users get the
    // paywall instead of the form.
    guard(Feature.CUSTOM_EXERCISES, () => {
      showModal(SportActivityFormDialog, {});
    });
  };

  const handleItemPress = (activity: SportActivityRecord) => {
    showModal(SportActivityFormDialog, {
      sportActivityId: activity.id,
    });
  };

  const isEmpty = activities.length === 0;

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center justify-between mb-3">
        <View className="flex flex-row items-center gap-3">
          <View className="h-0.5 w-6 rounded-sm bg-accent" />
          <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
            {t('exerciseLibrary.title')}
          </Text>
        </View>
        <Pressable
          onPress={handleAdd}
          className="flex flex-row items-center gap-1.5"
        >
          <Icon name="Plus" size={14} className="text-accent" />
          {/* testID on the Text leaf, not the transparent parent Pressable: Fabric
              view-flattening drops accessibilityIdentifier from layout-only/transparent
              Pressables, but a text-drawing leaf keeps it — so Maestro reliably resolves
              (and taps, landing inside the Pressable's bounds → fires onPress). */}
          <Text
            testID="exercise-library-add"
            className="font-inter-medium text-xs text-accent"
          >
            {t('exerciseLibrary.add')}
          </Text>
        </Pressable>
      </View>

      <View className="rounded-xl bg-surface overflow-hidden">
        {isEmpty ? (
          <View className="py-8">
            <Text className="font-inter-regular text-text-muted text-center text-sm">
              {t('exerciseLibrary.empty')}
            </Text>
          </View>
        ) : (
          activities.map((activity, index) => (
            <SportActivityListItem
              key={activity.id}
              activity={activity}
              onPress={() => handleItemPress(activity)}
              showBottomBorder={index < activities.length - 1}
            />
          ))
        )}
      </View>
    </View>
  );
};
